import React, { useMemo } from 'react';
import styled from 'styled-components';
import { rem } from '@packages/ui/tokens/spacing';
import { convertNumberWithCommas } from '@packages/shared/utils';
import type {
  TableFooterConfig,
  FooterColumnDef,
  FooterAggregateFn,
  InternalColumnDef,
  FormatConfig,
} from '../schema/types';

const StyledTfoot = styled.tfoot<{ $highlighted: boolean }>`
  tr td {
    padding: ${rem(14)} ${rem(10)} ${rem(14)} ${rem(3)};
    font-weight: 700;
    font-size: ${rem(14)};
    line-height: ${rem(18)};
    color: var(--ui-table-cell-color, #161616);
    border-top: ${rem(1)} solid var(--ui-table-border, var(--ui-border-muted));
    white-space: nowrap;

    ${({ $highlighted }) =>
      $highlighted &&
      `background: var(--ui-table-group-bg, var(--ui-background-muted, #f7f7f7));`}
  }

  tr td.dt-v2-summary-label {
    color: var(--ui-text-muted, #717171);
    font-weight: 500;
  }
`;

interface FooterRendererProps {
  config: Readonly<TableFooterConfig>;
  visibleColumns: ReadonlyArray<InternalColumnDef>;
  data: ReadonlyArray<Record<string, unknown>>;
  /** Pre-computed values keyed by `sourceKey` (e.g. server-provided totals). */
  footerData?: Record<string, unknown>;
  showCheckbox: boolean;
  showActionsColumn: boolean;
  subRowOverflowCols?: number;
}

function computeAggregate(
  data: ReadonlyArray<Record<string, unknown>>,
  field: string,
  fn: FooterAggregateFn,
): number | null {
  const values = data
    .map((row) => {
      const v = row[field];
      return typeof v === 'number' ? v : Number(v);
    })
    .filter((n) => !isNaN(n));

  if (values.length === 0) return null;

  switch (fn) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'average':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'count':
      return values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default: {
      const _exhaustive: never = fn;
      return _exhaustive;
    }
  }
}

function formatNumericValue(
  value: number | null | undefined,
  format?: FormatConfig,
  prefix?: string,
  suffix?: string,
): string {
  if (value == null || isNaN(value)) return '-';
  const decimals = format?.decimals ?? 2;
  const formatted = convertNumberWithCommas(value, true, decimals);
  return `${prefix ?? ''}${formatted}${suffix ?? ''}`;
}

interface CellDisplay {
  labelText?: string;
  valueText?: string;
  isLabelOnly: boolean;
}

function resolveCellDisplay(
  def: FooterColumnDef,
  data: ReadonlyArray<Record<string, unknown>>,
  footerData?: Record<string, unknown>,
): CellDisplay {
  let numericValue: number | null = null;

  if (def.sourceKey != null) {
    const raw = footerData?.[def.sourceKey] ?? null;
    numericValue = raw != null ? Number(raw) : null;
  } else if (def.aggregate != null) {
    numericValue = computeAggregate(data, def.field, def.aggregate);
  }

  const hasValue = def.sourceKey != null || def.aggregate != null;
  const valueText = hasValue ? formatNumericValue(numericValue, def.format, def.prefix, def.suffix) : undefined;

  if (!hasValue && def.label != null) {
    return { labelText: def.label, isLabelOnly: true };
  }

  return { labelText: def.label, valueText, isLabelOnly: false };
}

export function FooterRenderer({
  config,
  visibleColumns,
  data,
  footerData,
  showCheckbox,
  showActionsColumn,
  subRowOverflowCols = 0,
}: FooterRendererProps): React.ReactElement {
  const cellMap = useMemo(() => {
    const map = new Map<string, { display: CellDisplay; align: string }>();

    for (const def of config.columns) {
      const display = resolveCellDisplay(def, data, footerData);
      const matchedCol = visibleColumns.find((c) => c.field === def.field);
      map.set(def.field, { display, align: matchedCol?.align ?? 'left' });
    }

    return map;
  }, [config.columns, data, footerData, visibleColumns]);

  return (
    <StyledTfoot $highlighted={config.style === 'highlighted'} data-testid='dt-v2-footer'>
      <tr>
        {showCheckbox && <td />}
        {visibleColumns.map((col) => {
          const cell = cellMap.get(col.field);
          if (!cell) return <td key={col.field} />;

          const { display } = cell;
          return (
            <td
              key={col.field}
              className={display.isLabelOnly ? 'dt-v2-summary-label' : undefined}
              style={{ textAlign: cell.align as React.CSSProperties['textAlign'] }}
            >
              {display.labelText != null && (
                <span className={display.isLabelOnly ? undefined : 'dt-v2-summary-label'}>
                  {display.labelText}
                  {display.valueText != null ? ': ' : ''}
                </span>
              )}
              {display.valueText != null && <span>{display.valueText}</span>}
            </td>
          );
        })}
        {subRowOverflowCols > 0 &&
          Array.from({ length: subRowOverflowCols }, (_, i) => (
            <td key={`sub-overflow-${i}`} />
          ))}
        {showActionsColumn && <td />}
      </tr>
    </StyledTfoot>
  );
}
